import React from "react";
import { useTranslation } from "react-i18next";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChevronDown, ChevronUp, Pencil, Trash2 } from "lucide-react";
import type { ScorecardField } from "@/types/scorecard";

interface FieldRowProps {
	field: ScorecardField;
	isFirst: boolean;
	isLast: boolean;
	typeLabel: string;
	onEdit: (field: ScorecardField) => void;
	onDelete: (field: ScorecardField) => void;
	onMove: (field: ScorecardField, direction: -1 | 1) => void;
}

/** One row of the fields table; memoized so reorder swaps re-render only the rows involved. */
const FieldRow: React.FC<FieldRowProps> = React.memo(
	({ field, isFirst, isLast, typeLabel, onEdit, onDelete, onMove }) => {
		const { t } = useTranslation();
		return (
			<TableRow>
				<TableCell className="font-medium text-foreground">
					{field.name}
					{field.required && (
						<span className="ml-1 text-destructive" aria-hidden="true">
							*
						</span>
					)}
				</TableCell>
				<TableCell>
					{field.category ? (
						<span className="flex items-center gap-2 text-sm">
							<span
								className="h-2.5 w-2.5 shrink-0 rounded-full"
								style={{ backgroundColor: field.category.color ?? undefined }}
								aria-hidden="true"
							/>
							{field.category.name}
						</span>
					) : (
						<span className="text-sm text-muted-foreground">
							{t("admin.scorecardFields.noCategory", "No category")}
						</span>
					)}
				</TableCell>
				<TableCell>
					<Badge variant="outline">{typeLabel}</Badge>
				</TableCell>
				<TableCell className="hidden max-w-xs truncate text-sm text-muted-foreground md:table-cell">
					{field.description || "—"}
				</TableCell>
				<TableCell>
					<div className="flex items-center justify-end">
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7"
									disabled={isFirst}
									onClick={() => onMove(field, -1)}
									aria-label={t("admin.scorecardFields.moveUp", "Move up")}
								>
									<ChevronUp className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>{t("admin.scorecardFields.moveUp", "Move up")}</TooltipContent>
						</Tooltip>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7"
									disabled={isLast}
									onClick={() => onMove(field, 1)}
									aria-label={t("admin.scorecardFields.moveDown", "Move down")}
								>
									<ChevronDown className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>{t("admin.scorecardFields.moveDown", "Move down")}</TooltipContent>
						</Tooltip>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7"
									onClick={() => onEdit(field)}
									aria-label={t("admin.scorecardFields.editField", "Edit field")}
								>
									<Pencil className="h-3.5 w-3.5" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>{t("admin.scorecardFields.editField", "Edit field")}</TooltipContent>
						</Tooltip>
						<AlertDialog>
							<Tooltip>
								<TooltipTrigger asChild>
									<AlertDialogTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7 text-muted-foreground hover:text-destructive"
											aria-label={t("admin.scorecardFields.deleteField", "Delete field")}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</AlertDialogTrigger>
								</TooltipTrigger>
								<TooltipContent>
									{t("admin.scorecardFields.deleteField", "Delete field")}
								</TooltipContent>
							</Tooltip>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>
										{t("admin.scorecardFields.deleteField", "Delete field")}
									</AlertDialogTitle>
									<AlertDialogDescription>
										{t(
											"admin.scorecardFields.deleteFieldConfirm",
											'Delete "{{name}}"? Evaluators will no longer see it; answers already saved for it stay in existing scorecards but are no longer shown.',
											{ name: field.name },
										)}
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
									<AlertDialogAction
										onClick={() => onDelete(field)}
										className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
									>
										{t("common.delete", "Delete")}
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				</TableCell>
			</TableRow>
		);
	},
);
FieldRow.displayName = "FieldRow";

interface FieldsTableProps {
	fields: ScorecardField[];
	onEdit: (field: ScorecardField) => void;
	onDelete: (field: ScorecardField) => void;
	onMove: (field: ScorecardField, direction: -1 | 1) => void;
}

/** display_order-sorted table of all scorecard field definitions. */
export const FieldsTable: React.FC<FieldsTableProps> = ({ fields, onEdit, onDelete, onMove }) => {
	const { t } = useTranslation();

	const typeLabels: Record<string, string> = {
		rating: t("admin.scorecardFields.types.rating", "Rating scale"),
		text: t("admin.scorecardFields.types.text", "Text"),
		textarea: t("admin.scorecardFields.types.textarea", "Long text"),
		number: t("admin.scorecardFields.types.number", "Number"),
		boolean: t("admin.scorecardFields.types.boolean", "Yes / No"),
		dropdown: t("admin.scorecardFields.types.dropdown", "Dropdown"),
	};

	return (
		<div className="overflow-x-auto rounded-sm border border-border bg-card">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>{t("admin.scorecardFields.fieldName", "Name")}</TableHead>
						<TableHead>{t("admin.scorecardFields.category", "Category")}</TableHead>
						<TableHead>{t("admin.scorecardFields.fieldType", "Type")}</TableHead>
						<TableHead className="hidden md:table-cell">
							{t("admin.scorecardFields.fieldDescriptionColumn", "Description")}
						</TableHead>
						<TableHead className="text-right">
							{t("admin.scorecardFields.actions", "Actions")}
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{fields.map((field, index) => (
						<FieldRow
							key={field.id}
							field={field}
							isFirst={index === 0}
							isLast={index === fields.length - 1}
							typeLabel={typeLabels[field.field_type] ?? field.field_type}
							onEdit={onEdit}
							onDelete={onDelete}
							onMove={onMove}
						/>
					))}
				</TableBody>
			</Table>
		</div>
	);
};
